import React, { useState, useEffect, useRef, useCallback } from 'react';
import { List as ListIcon, Plus, Trash2, Link as LinkIcon, CornerDownRight, FolderTree, Eye, EyeOff, ArrowUp, ArrowDown, Edit, X, Tag, GitMerge, Settings, Save } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import catalogService from '../../services/catalogService';
import menuService from '../../services/menuService';
import itemService from '../../services/itemService';
import { toast } from 'react-toastify';
import { getImageUrl } from '../../utils/helpers';
const sortMenus = (list) => {
    if (!list || !Array.isArray(list)) return [];
    return [...list]
        .sort((a, b) => {
            const orderA = a.menuOrder !== undefined && a.menuOrder !== null ? a.menuOrder : (a.order || 0);
            const orderB = b.menuOrder !== undefined && b.menuOrder !== null ? b.menuOrder : (b.order || 0);
            return orderA - orderB;
        })
        .map(m => ({
            ...m,
            subMenus: sortMenus(m.subMenus)
        }));
};

const AdminMenuList = () => {
    const [menus, setMenus] = useState([]);
    const [purposes, setPurposes] = useState([]); 
    const [loading, setLoading] = useState(false);
    const [homepageItemCounts, setHomepageItemCounts] = useState({});
    
    // STATES FORM
    const [menuName, setMenuName] = useState("");
    const [menuUrl, setMenuUrl] = useState("");
    const [menuPosition, setMenuPosition] = useState("HEADER"); 
    
    const [isLeaf, setIsLeaf] = useState(false); 
    const [selectedParentId, setSelectedParentId] = useState(""); 
    const [selectedPurposeId, setSelectedPurposeId] = useState(""); 
    const [isShownInMainPage, setIsShownInMainPage] = useState(false); 

    const [viewTab, setViewTab] = useState("HEADER");

    const [selectedIcon, setSelectedIcon] = useState(null); 
    const [previewUrl, setPreviewUrl] = useState(null);   

    const [editingId, setEditingId] = useState(null);
    
    const [scrollTargetId, setScrollTargetId] = useState(null);

    const formRef = useRef(null);
    const fileInputRef = useRef(null);
    const [maxItemsDisplay, setMaxItemsDisplay] = useState("");

    const [productDetailConfig, setProductDetailConfig] = useState({
        product_detail_bestseller_limit: '10',
        product_detail_similar_limit: '10',
        home_hot_deal_limit: '10',
        related_news_limit: '5',
    });
    const [savingConfig, setSavingConfig] = useState(false);

    const resetForm = () => {
        setMenuName(""); 
        setMenuUrl("");
        setSelectedParentId("");
        setSelectedPurposeId("");
        setMaxItemsDisplay("");
        setIsShownInMainPage(false);
        setIsLeaf(false);
        setSelectedIcon(null);
        setPreviewUrl(null);
        setEditingId(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };
    
    const fetchData = useCallback(async (isBackgroundLoad = false) => {
        if (!isBackgroundLoad) setLoading(true);
        try {
            const timestamp = new Date().getTime(); 
            // 3. THÊM API GỌI DỮ LIỆU TRANG CHỦ VÀO Promise.all
            const [menuRes, purposeRes, configRes, homepageGroupsRes] = await Promise.all([
                menuService.getAllMenus(timestamp),
                adminStaffService.getPurposes(timestamp).catch(() => []),
                catalogService.getSystemConfig().catch(() => ({})),
                itemService.getHomepageMenuGroups().catch(() => []) 
            ]);
            
            const rawMenus = menuRes?.data || menuRes || [];
            setMenus(sortMenus(rawMenus));
            
            const safePurposes = purposeRes?.data || purposeRes || [];
            const sortedPurposes = [...safePurposes].sort((a, b) => (a.purposeOrder || 0) - (b.purposeOrder || 0));
            setPurposes(sortedPurposes);

            const conf = configRes?.data || configRes || {};
            setProductDetailConfig({
                product_detail_bestseller_limit: conf.product_detail_bestseller_limit || '10',
                product_detail_similar_limit: conf.product_detail_similar_limit || '10',
                home_hot_deal_limit: conf.home_hot_deal_limit || '10',
                related_news_limit: conf.related_news_limit || '5', 
            });

            // 4. XỬ LÝ ĐẾM SỐ LƯỢNG SẢN PHẨM TỪ API VÀ LƯU VÀO STATE
            const hpGroups = homepageGroupsRes?.data || homepageGroupsRes || [];
            const counts = {};
            hpGroups.forEach(group => {
                if (group.menuId) {
                    // Nếu group.items tồn tại, lấy độ dài (length), không thì cho bằng 0
                    counts[group.menuId] = group.items ? group.items.length : 0;
                }
            });
            setHomepageItemCounts(counts);
            
        } catch (error) { 
            console.error(error); 
        } finally { 
            if (!isBackgroundLoad) setLoading(false); 
        }
    }, []);

    useEffect(() => { 
        fetchData(false); 
    }, [fetchData]);

    useEffect(() => {
        if (scrollTargetId) {
            const timer = setTimeout(() => {
                const element = document.getElementById(`row-menu-${scrollTargetId}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    const originalBg = element.style.backgroundColor;
                    element.style.transition = 'background-color 0.5s ease';
                    element.style.backgroundColor = '#ffedd5'; 
                    
                    setTimeout(() => {
                        element.style.backgroundColor = originalBg;
                    }, 2000);
                }
                setScrollTargetId(null); 
            }, 400); 
            
            return () => clearTimeout(timer);
        }
    }, [scrollTargetId, menus]);

    const handleSaveProductDetailConfig = async () => {
        setSavingConfig(true);
        try {
            await adminStaffService.updateSystemConfig({
                ...productDetailConfig,
            });
            toast.success("Đã lưu cấu hình hệ thống!");
        } catch {
            toast.error("Lỗi khi lưu cấu hình!");
        } finally {
            setSavingConfig(false);
        }
    };

    const flattenCategoryMenus = (menuList, level = 0, result = []) => {
        menuList.forEach(m => {
            if (m.menuPosition === 'CATEGORY') {
                result.push({ id: m.menuId, name: m.menuName, level, menuUrl: m.menuUrl });
                if (m.subMenus && m.subMenus.length > 0) {
                    flattenCategoryMenus(m.subMenus, level + 1, result);
                }
            }
        });
        return result;
    };
    const flatCatMenus = flattenCategoryMenus(menus);

    const handleSubmitForm = async (e) => {
        e.preventDefault();
        if (!menuName.trim()) return;

        let finalUrl = menuUrl;
        let finalParentId = null;

        if (menuPosition === 'CATEGORY') {
            finalParentId = selectedParentId ? parseInt(selectedParentId) : null;
            if (isLeaf) {
                if (!selectedPurposeId) {
                    toast.warning("Vui lòng chọn 'Nhu Cầu' cho Danh mục Lá!");
                    return;
                }
                if (finalParentId) {
                    const parent = flatCatMenus.find(m => m.id === finalParentId);
                    const baseUrl = parent?.menuUrl?.split('?')[0] || `/menu/${finalParentId}`;
                    finalUrl = `${baseUrl}?purpose=${selectedPurposeId}`;
                } else {
                    finalUrl = `?purpose=${selectedPurposeId}`;
                }
            } else {
                finalUrl = ""; 
            }
        } else {
            if (!menuUrl.trim()) {
                toast.warning("Vui lòng nhập đường dẫn URL!");
                return;
            }
        }

        const formData = new FormData();
        const menuData = {
            menuName,
            menuUrl: finalUrl,
            menuPosition,
            isShownInMainPage,
            maxItemsDisplay: maxItemsDisplay ? parseInt(maxItemsDisplay) : null,
            parentId: finalParentId
        };

        formData.append("menu", new Blob([JSON.stringify(menuData)], { type: "application/json" }));
        
        if (selectedIcon) {
            formData.append("icon", selectedIcon);
        }

        try {
            setLoading(true);
            
            const idToScroll = editingId; 
            
            if (editingId) {
                await adminStaffService.updateMenu(editingId, formData);
                toast.success("Cập nhật menu thành công! ✨");
            } else {
                await adminStaffService.createMenu(formData);
                toast.success("Thêm menu thành công! ✨");
            }
            
            resetForm();
            await fetchData(true);
            
            if (idToScroll) {
                setScrollTargetId(idToScroll);
            }

        } catch (error) { 
            toast.error(editingId ? "Lỗi cập nhật menu!" : "Lỗi thêm menu!"); 
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (menu, passedParentId = "") => {
        setEditingId(menu.menuId);
        setMenuName(menu.menuName);
        setMenuUrl(menu.menuUrl || "");
        setMenuPosition(menu.menuPosition);
        setMaxItemsDisplay(menu.maxItemsDisplay || "");
        setViewTab(menu.menuPosition);
        setIsShownInMainPage(menu.isShownInMainPage || menu.shownInMainPage || false);
        
        setSelectedIcon(null); 
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; 
        }

        if (menu.menuIcon) {
            setPreviewUrl(getImageUrl(menu.menuIcon));
        } else {
            setPreviewUrl(null);
        }

        if (menu.menuPosition === 'CATEGORY') {
            if (menu.menuUrl && menu.menuUrl.includes('?purpose=')) {
                setIsLeaf(true);
                const purpose = menu.menuUrl.split('?purpose=')[1];
                setSelectedPurposeId(String(purpose));
            } else {
                setIsLeaf(false);
                setSelectedPurposeId("");
            }
            
            const finalParentId = passedParentId || menu.parentId || menu.parent?.menuId || "";
            setSelectedParentId(String(finalParentId)); 
        }

        if (formRef.current) {
            setTimeout(() => {
                formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    };

    const handleDelete = async (id) => {
        if(window.confirm("Bạn có chắc muốn xóa Menu này? (Các Menu con sẽ bị xóa theo toàn bộ)")) {
            try { 
                await adminStaffService.deleteMenu(id); 
                toast.success("Xóa Menu thành công! 🎉"); 
                await fetchData(true); 
            } 
            catch (error) { 
                const errorMsg = error.response?.data || "Lỗi xóa menu! Vui lòng thử lại.";
                toast.error(errorMsg); 
            }
        }
    };

    const handleToggleMainPage = async (id) => {
        try { 
            await adminStaffService.toggleMenuMainPage(id); 
            fetchData(true); 
        } 
        catch (error) { console.error(error); }
    };

    const handleMove = async (currentList, index, direction) => {
        if (direction === 'UP' && index === 0) return;
        if (direction === 'DOWN' && index === currentList.length - 1) return;

        const newList = [...currentList];
        const swapIndex = direction === 'UP' ? index - 1 : index + 1;
        
        [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];

        const payload = newList.map((item, i) => ({ id: item.menuId, order: i }));

        try {
            await adminStaffService.reorderMenus(payload);
            fetchData(true); 
        } catch (e) { toast.error("Lỗi khi đổi vị trí!"); }
    };

    const renderActionButtons = (menu, parentId = "") => {
        const isShown = menu.isShownInMainPage === true || menu.shownInMainPage === true;
        const isCategory = menu.menuPosition === 'CATEGORY'; 
        const displayLimit = menu.maxItemsDisplay > 0 ? menu.maxItemsDisplay : '∞';
        
        // Lấy số lượng thực tế ra (Nếu chưa có dữ liệu thì tạm hiện 0)
        const actualCount = homepageItemCounts[menu.menuId] !== undefined ? homepageItemCounts[menu.menuId] : 0;

        return (
            <div className="flex justify-end gap-2 items-center">
                <button 
                    onClick={() => handleToggleMainPage(menu.menuId)}
                    title={isShown ? "Nhấn để Ẩn khỏi trang chủ" : "Nhấn để Hiện ra trang chủ"}
                    className={`px-2 py-1.5 flex items-center gap-1.5 rounded text-xs font-bold transition-all shadow-sm ${isShown ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200 hover:text-green-600'}`}
                >
                    {isShown ? (
                        <>
                            <Eye size={14} className="shrink-0"/> 
                            <span className="whitespace-nowrap">Hiện trang chủ</span>
                            {isCategory && (
                                <span className="bg-green-200 text-green-800 px-1.5 py-0.5 rounded-sm text-[10px] leading-none ml-0.5 whitespace-nowrap" title={`Đang hiện ${actualCount} SP / Tối đa ${displayLimit}`}>
                                    {actualCount} / {displayLimit}
                                </span>
                            )}
                        </>
                    ) : (
                        <>
                            <EyeOff size={14} className="shrink-0"/> 
                            <span className="whitespace-nowrap">Ẩn</span>
                        </>
                    )}
                </button>
                
                {/* 2 nút Sửa / Xóa được giữ nguyên trên cùng 1 hàng */}
                <button onClick={() => handleEditClick(menu, parentId)} className="bg-blue-50 text-blue-500 px-2 py-1.5 rounded hover:bg-blue-100 transition-colors" title="Sửa">
                    <Edit size={16} />
                </button>

                <button onClick={() => handleDelete(menu.menuId)} className="bg-red-50 text-red-500 px-2 py-1.5 rounded hover:bg-red-100 transition-colors" title="Xóa">
                    <Trash2 size={16} />
                </button>
            </div>
        );
    }

    const renderFlatRows = (position) => {
        const list = menus.filter(m => m.menuPosition === position);
        if (list.length === 0) return <tr><td colSpan="4" className="text-center py-8 text-gray-500">Chưa có Menu nào.</td></tr>;
        return list.map((menu, index) => (
            <tr id={`row-menu-${menu.menuId}`} key={menu.menuId} className="border-b border-gray-50 hover:bg-orange-50/50 transition-colors duration-500">
                <td className="px-6 py-4 font-bold text-gray-400">#{menu.menuId}</td>
                <td className="px-6 py-4 font-bold text-gray-800 flex items-center gap-3">
                    <div className="flex flex-col opacity-30 hover:opacity-100 transition-opacity">
                        <button disabled={index === 0} onClick={() => handleMove(list, index, 'UP')} className="hover:text-blue-600 disabled:text-gray-200"><ArrowUp size={14}/></button>
                        <button disabled={index === list.length - 1} onClick={() => handleMove(list, index, 'DOWN')} className="hover:text-blue-600 disabled:text-gray-200"><ArrowDown size={14}/></button>
                    </div>
                    <div className="flex items-center gap-2">
                        {menu.menuIcon && (
                            <img src={getImageUrl(menu.menuIcon)} alt="" className="w-6 h-6 object-contain" />
                        )}
                        <span className="font-bold text-gray-800">{menu.menuName}</span>
                    </div>
                </td>
                <td className="px-6 py-4"><span className="bg-blue-50 text-blue-600 px-2 py-1 rounded text-xs font-mono">{menu.menuUrl}</span></td>
                <td className="px-6 py-4">{renderActionButtons(menu)}</td>
            </tr>
        ));
    };

    const renderHierarchicalRows = (menuList, level = 0, currentParentId = "") => {
        const catList = level === 0 ? menuList.filter(m => m.menuPosition === 'CATEGORY') : menuList;
        if (level === 0 && catList.length === 0) return <tr><td colSpan="4" className="text-center py-8 text-gray-500">Chưa có Danh mục nào.</td></tr>;
        return catList.map((menu, index) => (
            <React.Fragment key={menu.menuId}>
                <tr id={`row-menu-${menu.menuId}`} className="border-b border-gray-50 hover:bg-orange-50/50 transition-colors duration-500">
                    <td className="px-6 py-4 font-bold text-gray-400 w-20">#{menu.menuId}</td>
                    <td className="px-6 py-4 flex items-center gap-2">
                        <div style={{ paddingLeft: `${level * 24}px` }} className="flex items-center gap-2">
                            <div className="flex flex-col opacity-30 hover:opacity-100 transition-opacity mr-2">
                                <button disabled={index === 0} onClick={() => handleMove(catList, index, 'UP')} className="hover:text-blue-600 disabled:text-gray-200"><ArrowUp size={14}/></button>
                                <button disabled={index === catList.length - 1} onClick={() => handleMove(catList, index, 'DOWN')} className="hover:text-blue-600 disabled:text-gray-200"><ArrowDown size={14}/></button>
                            </div>
                            {level > 0 && <CornerDownRight size={16} className="text-gray-300" />}
                            {menu.menuIcon && (
                                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                                    <img src={getImageUrl(menu.menuIcon)} alt="" className="max-w-full max-h-full object-contain" />
                                </div>
                            )}
                            <span className={level === 0 ? "font-bold text-tnc-orange" : "font-medium text-gray-700"}>
                                {menu.menuName}
                            </span>
                        </div>
                    </td>
                    <td className="px-6 py-4"><span className="bg-blue-50 text-blue-600 px-2 py-1 rounded text-xs font-mono">{menu.menuUrl}</span></td>
                    <td className="px-6 py-4">{renderActionButtons(menu, currentParentId)}</td>
                </tr>
                {menu.subMenus && menu.subMenus.length > 0 && renderHierarchicalRows(menu.subMenus, level + 1, menu.menuId)}
            </React.Fragment>
        ));
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-6">
                <ListIcon size={24} className="text-tnc-orange"/> Quản lý Hệ thống Menu
            </h2>

            <div ref={formRef} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8 scroll-mt-24">
                <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
                    <h3 className="font-bold text-gray-800">
                        {editingId ? `Cập nhật Menu #${editingId}` : 'Thêm Menu Mới'}
                    </h3>
                    {editingId && (
                        <button onClick={resetForm} type="button" className="text-sm text-red-500 font-bold hover:text-red-700 flex items-center gap-1">
                            <X size={16}/> Hủy sửa
                        </button>
                    )}
                </div>
                <form onSubmit={handleSubmitForm} className="flex gap-4 items-end flex-wrap">
                    <div className="w-48">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Vị trí hiển thị *</label>
                        <select value={menuPosition} onChange={(e) => {
                            setMenuPosition(e.target.value);
                            setIsLeaf(false);
                            setSelectedParentId(""); 
                            setSelectedPurposeId("");
                        }} className="w-full border border-gray-200 rounded-md px-4 py-2 outline-none focus:border-tnc-orange font-bold text-tnc-orange bg-orange-50">
                            <option value="HEADER">Header</option>
                            <option value="FOOTER">Footer</option>
                            <option value="CATEGORY">Danh mục sản phẩm</option>
                        </select>
                    </div>

                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Tên hiển thị *</label>
                        <input type="text" value={menuName} onChange={(e) => setMenuName(e.target.value)} placeholder="VD: Laptop Gaming..." required className="w-full border border-gray-200 rounded-md px-4 py-2 outline-none focus:border-tnc-orange" />
                    </div>

                    {menuPosition === 'CATEGORY' && (
                        <div className="w-full flex gap-4 mt-2 bg-blue-50/50 p-4 rounded-lg border border-blue-100 items-end flex-wrap">
                            <div className="w-full sm:w-1/2 md:w-1/3">
                                <label className="block text-sm font-bold text-blue-800 mb-1 flex items-center gap-1"><FolderTree size={14}/> Nằm trong (Menu Cha)</label>
                                <select value={selectedParentId} onChange={(e) => setSelectedParentId(e.target.value)} className="w-full border border-gray-200 rounded-md px-4 py-2 outline-none focus:border-blue-500">
                                    <option value="">-- Menu Gốc (Không có cha) --</option>
                                    {flatCatMenus
                                        .filter(m => !m.menuUrl || !m.menuUrl.includes('?purpose='))
                                        .map(m => (
                                        <option key={m.id} value={m.id}>
                                            {"—".repeat(m.level)} {m.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="w-full mb-2 border-t border-blue-100 pt-3 mt-1 flex flex-col gap-3">
                                <span className="text-sm font-bold text-gray-700">Loại danh mục:</span>
                                <div className="flex flex-wrap items-center gap-6">
                                    <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-md border border-gray-200 hover:border-blue-400">
                                        <input type="radio" checked={!isLeaf} onChange={() => setIsLeaf(false)} className="accent-blue-600"/>
                                        <span className="text-sm font-medium text-gray-700">Danh mục Thường (Có thể chứa menu con)</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-md border border-gray-200 hover:border-blue-400">
                                        <input type="radio" checked={isLeaf} onChange={() => setIsLeaf(true)} className="accent-blue-600"/>
                                        <span className="text-sm font-medium text-gray-700">Danh mục Lá (Có Lọc Nhu Cầu)</span>
                                    </label>
                                </div>
                            </div>

                            {isLeaf ? (
                                <div className="w-full flex gap-4 items-center bg-white p-3 rounded-md border border-orange-200">
                                    <div className="flex-1 max-w-[300px]">
                                        <label className="block text-sm font-bold text-orange-600 mb-1 flex items-center gap-1"><Tag size={14}/> Chọn Nhu Cầu Tương Ứng *</label>
                                        <select required={isLeaf} value={selectedPurposeId} onChange={(e) => setSelectedPurposeId(e.target.value)} className="w-full border border-orange-200 rounded-md px-4 py-2 outline-none focus:border-tnc-orange">
                                            <option value="">-- Click để chọn --</option>
                                            {purposes.map(p => (
                                                <option key={p.purposeId} value={p.purposeId}>{p.purposeName}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex-1 text-xs text-green-600 italic">
                                        <GitMerge size={12} className="inline mr-1"/> URL sẽ tự động nối Nhu cầu vào cuối.
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full text-sm text-green-600 font-medium italic mt-2">
                                    <GitMerge size={14} className="inline mr-1"/> URL sẽ được hệ thống tạo tự động ngay sau khi lưu!
                                </div>
                            )}
                        </div>
                    )}

                    {menuPosition !== 'CATEGORY' && (
                        <div className="flex-1 min-w-[250px]">
                            <label className="block text-sm font-bold text-gray-700 mb-1">Đường dẫn (URL) *</label>
                            <div className="relative">
                                <span className="absolute left-3 top-2.5 text-gray-400"><LinkIcon size={16}/></span>
                                <input type="text" value={menuUrl} onChange={(e) => setMenuUrl(e.target.value)} placeholder="/gioi-thieu, /tin-tuc" required className="w-full border border-gray-200 rounded-md pl-9 pr-4 py-2 outline-none focus:border-tnc-orange text-sm font-mono text-blue-600" />
                            </div>
                        </div>
                    )}

                    {menuPosition !== 'FOOTER' && (
                        <div className="w-full mt-4 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300 flex items-center gap-6">
                            <div className="flex-1">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Icon menu (Tùy chọn)</label>
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    ref={fileInputRef}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        setSelectedIcon(file);
                                        if (file) setPreviewUrl(URL.createObjectURL(file));
                                    }}
                                    className="text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-tnc-orange hover:file:bg-orange-100"
                                />
                            </div>
                            {previewUrl && (
                                <div className="relative w-12 h-12 bg-white rounded-lg border p-1 shadow-sm">
                                    <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
                                    <button 
                                        type="button" 
                                        onClick={() => { setSelectedIcon(null); setPreviewUrl(null); }}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="w-full flex items-center justify-between mt-4 border-t border-gray-100 pt-4">
                        <div className="flex flex-col gap-3">
                            <label className="flex items-center gap-2 cursor-pointer border border-gray-200 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors w-max">
                                <input 
                                    type="checkbox" 
                                    checked={isShownInMainPage} 
                                    onChange={(e) => setIsShownInMainPage(e.target.checked)} 
                                    className="w-4 h-4 text-tnc-orange rounded cursor-pointer" 
                                />
                                <span className="text-sm font-bold text-gray-700">Hiển thị ra Trang chủ</span>
                            </label>
                            
                            {menuPosition === 'CATEGORY' && isShownInMainPage && (
                                <div className="flex items-center gap-2 bg-blue-50 p-2 rounded-md border border-blue-100 w-max">
                                    <label className="text-sm font-bold text-blue-800">Số SP hiển thị tối đa:</label>
                                    <input 
                                        type="number" 
                                        value={maxItemsDisplay} 
                                        onChange={(e) => setMaxItemsDisplay(e.target.value)} 
                                        placeholder="Trống = Vô hạn" 
                                        min="1"
                                        className="w-36 border border-blue-200 rounded px-2 py-1 outline-none focus:border-blue-500 text-sm font-bold text-blue-700" 
                                    />
                                </div>
                            )}
                        </div>

                        <button type="submit" className={`px-8 py-2.5 rounded-md font-bold flex items-center gap-2 transition-colors shadow-md text-white h-max ${editingId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-tnc-orange hover:bg-tnc-orange-hover'}`}>
                            {editingId ? <Edit size={18} /> : <Plus size={18} />} 
                            {editingId ? 'Lưu Thay Đổi' : 'Lưu Menu'}
                        </button>
                    </div>
                    
                </form>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
                <div className="flex bg-gray-50 border-b border-gray-200">
                    <button onClick={() => setViewTab('HEADER')} className={`flex-1 py-3 font-bold text-sm transition-colors ${viewTab === 'HEADER' ? 'bg-white text-tnc-orange border-t-2 border-t-tnc-orange' : 'text-gray-500 hover:text-gray-700'}`}>Menu Header</button>
                    <button onClick={() => setViewTab('CATEGORY')} className={`flex-1 py-3 font-bold text-sm flex items-center justify-center gap-2 transition-colors ${viewTab === 'CATEGORY' ? 'bg-white text-tnc-orange border-t-2 border-t-tnc-orange' : 'text-gray-500 hover:text-gray-700'}`}>
                        <FolderTree size={16}/> Menu Danh Mục (Phân cấp)
                    </button>
                    <button onClick={() => setViewTab('FOOTER')} className={`flex-1 py-3 font-bold text-sm transition-colors ${viewTab === 'FOOTER' ? 'bg-white text-tnc-orange border-t-2 border-t-tnc-orange' : 'text-gray-500 hover:text-gray-700'}`}>Menu Footer</button>
                </div>
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-white text-gray-500 text-sm border-b">
                            <th className="px-6 py-4 font-bold w-20">ID</th>
                            <th className="px-6 py-4 font-bold">Cấu trúc Menu</th>
                            <th className="px-6 py-4 font-bold">Đường dẫn (Link)</th>
                            <th className="px-6 py-4 font-bold text-right">Thao tác</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {loading ? <tr><td colSpan="4" className="text-center py-8 text-gray-500">Đang tải...</td></tr> : (viewTab === 'CATEGORY' ? renderHierarchicalRows(menus) : renderFlatRows(viewTab))}
                    </tbody>
                </table>
            </div>
            
            {/* CÀI ĐẶT HIỂN THỊ */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mt-6">
                <h3 className="font-bold text-gray-800 text-lg mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
                    <Settings size={20} className="text-tnc-orange" />
                    Cài đặt hiển thị hệ thống
                </h3>
                <div className="flex flex-wrap gap-6 items-end">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">
                            SP Bán chạy (Chi tiết SP)
                        </label>
                        <input
                            type="number" min="1" max="50"
                            value={productDetailConfig.product_detail_bestseller_limit}
                            onChange={e => setProductDetailConfig(p => ({
                                ...p, product_detail_bestseller_limit: e.target.value
                            }))}
                            className="w-full sm:w-32 border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-tnc-orange font-bold text-center"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">
                            SP Tương tự (Chi tiết SP)
                        </label>
                        <input
                            type="number" min="1" max="50"
                            value={productDetailConfig.product_detail_similar_limit}
                            onChange={e => setProductDetailConfig(p => ({
                                ...p, product_detail_similar_limit: e.target.value
                            }))}
                            className="w-full sm:w-32 border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-tnc-orange font-bold text-center"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">
                            SP Ưu đãi (Trang chủ)
                        </label>
                        <input
                            type="number" min="1" max="50"
                            value={productDetailConfig.home_hot_deal_limit}
                            onChange={e => setProductDetailConfig(p => ({
                                ...p, home_hot_deal_limit: e.target.value
                            }))}
                            className="w-full sm:w-32 border border-rose-200 bg-rose-50 rounded-lg px-3 py-2 outline-none focus:border-rose-500 text-rose-700 font-bold text-center"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">
                            Tin liên quan (Chi tiết Tin)
                        </label>
                        <input
                            type="number" min="1" max="20"
                            value={productDetailConfig.related_news_limit}
                            onChange={e => setProductDetailConfig(p => ({
                                ...p, related_news_limit: e.target.value
                            }))}
                            className="w-full sm:w-32 border border-blue-200 bg-blue-50 rounded-lg px-3 py-2 outline-none focus:border-blue-500 text-blue-700 font-bold text-center"
                        />
                    </div>

                    <button
                        onClick={handleSaveProductDetailConfig}
                        disabled={savingConfig}
                        className="bg-tnc-orange hover:bg-tnc-orange-hover text-white font-bold px-6 py-2 rounded-lg transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2 w-full sm:w-auto justify-center"
                    >
                        <Save size={16} /> {savingConfig ? 'Đang lưu...' : 'Lưu cài đặt'}
                    </button>
                </div>
            </div>
        </div>
    );
};
export default AdminMenuList;